import React, { useState, useEffect } from 'react';
import { Check, Zap, Building2, Repeat, Users, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_PLAN_IDS = new Set(['starter', 'professional', 'team']);

const normalizePlanId = (value) => {
  const normalized = (value || '').trim().toLowerCase();
  return PUBLIC_PLAN_IDS.has(normalized) ? normalized : '';
};

const getStoredPendingPlan = () => {
  try {
    if (typeof window === 'undefined') return '';
    return normalizePlanId(window.localStorage.getItem('aeo_pending_plan'));
  } catch {
    return '';
  }
};

const PricingSection = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const selectedPlanId = normalizePlanId(searchParams.get('plan')) || getStoredPendingPlan();
  // Display config (translated): order, style, labels (prices come from API)
  const PLAN_DISPLAY = {
    starter:      { order: 1, popular: false, icon: Zap,   period: t('pricing.lifetime'), description: t('pricing.plans.starter.desc'),      cta: t('pricing.plans.starter.cta') },
    professional: { order: 2, popular: true,  icon: Zap,   period: t('pricing.lifetime'), description: t('pricing.plans.professional.desc'), cta: t('pricing.plans.professional.cta') },
    team:         { order: 3, popular: false, icon: Users, period: t('pricing.lifetime'), description: t('pricing.plans.team.desc'),         cta: t('pricing.plans.team.cta') },
  };
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(null);
  const [couponPercent, setCouponPercent] = useState(0);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponVisible, setCouponVisible] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/checkout/validate-coupon`, {
        coupon_code: couponCode,
        email: user?.email || ''
      });
      if (res.data.valid) {
        setCouponValid(true);
        setCouponPercent(res.data.percent);
        setCouponMessage(res.data.description);
      } else {
        setCouponValid(false);
        setCouponPercent(0);
        setCouponMessage(res.data.message);
      }
    } catch {
      setCouponValid(false);
      setCouponMessage('Errore validazione codice');
    }
  };

  const handlePurchase = async (packageId) => {
    if (!user) {
      navigate(`/register?plan=${packageId}`);
      return;
    }
    try {
      localStorage.setItem('aeo_pending_plan', packageId);
      sessionStorage.setItem('aeo_pending_plan', packageId);
    } catch {}
    setLoading(true);
    try {
      const payload = {
        package_id: packageId,
        origin_url: window.location.origin,
        email: user.email
      };
      if (couponValid && couponCode.trim()) {
        payload.coupon_code = couponCode.trim();
      }
      const response = await axios.post(`${API_URL}/api/checkout/create`, payload);
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Errore durante il checkout. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscription = async (packageId) => {
    if (!user) {
      navigate('/login?redirect=/pricing');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/checkout/subscription`, {
        package_id: packageId,
        origin_url: window.location.origin,
        email: user.email
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Errore durante la sottoscrizione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  // Locale-aware features: try translation, fallback to API features (Italian)
  const localizedFeatures = (id, fallback) => {
    const translated = t(`pricing.plans.${id}.features`, { returnObjects: true });
    return Array.isArray(translated) && translated.length > 0 ? translated : (fallback || []);
  };

  // Build main plans from API data
  const mainPlans = ['starter', 'professional', 'team']
    .filter(id => packages[id])
    .map(id => ({
      id,
      pkg: packages[id],
      display: PLAN_DISPLAY[id] || { order: 99, popular: false, icon: Zap, period: '', description: '', cta: 'Acquista' }
    }))
    .sort((a, b) => a.display.order - b.display.order);

  // B2B package
  const b2b = packages['b2b_custom'];

  // Subscription packages
  const subBasic = packages['subscription_basic'];
  const subPro = packages['subscription_pro'];
  const selectedPlanPackage = selectedPlanId ? packages[selectedPlanId] : null;

  return (
    <section id="pricing" className="py-24 px-6 bg-[#0a0a0a]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8 animate-fade-up">
          <span className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-semibold">{t('pricing.eyebrow')}</span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-medium tracking-tight mt-4 mb-6">
            {t('pricing.title')}
          </h2>
          <p className="text-lg text-[#A1A1AA] max-w-2xl mx-auto">{t('pricing.subtitle')}</p>
        </div>

        {/* Trial Banner */}
        <div className="max-w-2xl mx-auto mb-12 p-6 rounded-2xl bg-gradient-to-r from-[#0055FF]/10 to-[#8B5CF6]/10 border border-[#0055FF]/20 text-center animate-fade-up" data-testid="trial-banner">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Sparkles className="w-5 h-5 text-[#0055FF]" />
            <h3 className="font-['Outfit'] text-xl font-medium">{t('pricing.trial.title')}</h3>
          </div>
          <p className="text-sm text-[#A1A1AA] mb-4">{t('pricing.trial.desc')}</p>
          <Link to="/register">
            <Button className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full px-8 py-3 text-sm font-medium group" data-testid="trial-cta-btn">
              {t('pricing.trial.cta')}
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>

        {selectedPlanPackage && (
          <div
            className="max-w-2xl mx-auto mb-12 rounded-2xl border border-[#FACC15]/30 bg-[#FACC15]/10 px-5 py-4 text-center animate-fade-up"
            data-testid="pricing-selected-plan"
            data-selected-plan={selectedPlanId}
          >
            <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[#FACC15]">
              Piano selezionato
            </span>
            <strong className="mt-1 block font-['Outfit'] text-xl font-semibold text-white">
              {selectedPlanPackage.name}
            </strong>
            <span className="mt-1 block text-sm text-[#A1A1AA]">
              Conferma il checkout da questa pagina quando sei pronto.
            </span>
          </div>
        )}

        {/* Coupon Code - Hidden by default */}
        <div className="max-w-md mx-auto mb-12 animate-fade-up text-center" data-testid="coupon-section">
          {!couponVisible && !couponValid ? (
            <button
              onClick={() => setCouponVisible(true)}
              className="text-sm text-[#A1A1AA] hover:text-white transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-white/50"
              data-testid="coupon-reveal-btn"
            >
              {t('pricing.coupon.reveal')}
            </button>
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={t('pricing.coupon.placeholder')}
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value); setCouponValid(null); setCouponMessage(''); }}
                  className="flex-1 bg-[#111111] border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-[#6b7280] focus:outline-none focus:border-[#3b82f6]"
                  data-testid="coupon-input"
                  autoFocus
                />
                <Button
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5 px-4"
                  onClick={validateCoupon}
                  disabled={!couponCode.trim()}
                  data-testid="coupon-validate-btn"
                >
                  {t('pricing.coupon.apply')}
                </Button>
              </div>
              {couponValid === true && (
                <div className="mt-2 px-3 py-2 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg text-sm text-[#10B981]" data-testid="coupon-valid">
                  {couponMessage} (-{couponPercent}%)
                </div>
              )}
              {couponValid === false && (
                <div className="mt-2 px-3 py-2 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg text-sm text-[#EF4444]" data-testid="coupon-invalid">
                  {couponMessage}
                </div>
              )}
            </>
          )}
        </div>

        {/* Main Pricing Cards - Dynamic from API */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {mainPlans.map(({ id, pkg, display }, index) => {
            const Icon = display.icon;
            const isSelected = selectedPlanId === id;
            return (
              <div
                key={id}
                className={`relative flex flex-col rounded-3xl p-8 animate-fade-up ${
                  display.popular
                    ? 'pricing-popular'
                    : 'bg-[#111111] border border-white/10'
                } ${isSelected ? 'ring-2 ring-[#FACC15]/70' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`pricing-card-${id}`}
                data-selected-plan={isSelected ? id : undefined}
              >
                {display.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#0055FF] text-sm font-medium">
                    {t('pricing.popular')}
                  </div>
                )}
                {isSelected && (
                  <div
                    className="absolute right-5 top-5 rounded-full border border-[#FACC15]/40 bg-[#FACC15]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#FACC15]"
                    data-testid={`pricing-selected-badge-${id}`}
                  >
                    Piano scelto
                  </div>
                )}

                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-6 h-6 text-[#0055FF]" />
                    <h3 className="font-['Outfit'] text-2xl font-medium">{pkg.name}</h3>
                  </div>
                  <p className="text-[#A1A1AA] text-sm">{display.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    {couponValid && couponPercent > 0 ? (
                      <>
                        <span className="text-4xl font-['Outfit'] font-semibold text-[#10B981]">{(pkg.price * (1 - couponPercent / 100)).toFixed(0)}EUR</span>
                        <span className="text-xl text-[#A1A1AA] line-through">{pkg.price}EUR</span>
                        <span className="text-xs bg-[#10B981]/20 text-[#10B981] px-2 py-0.5 rounded-full">-{couponPercent}%</span>
                      </>
                    ) : (
                      <span className="text-4xl font-['Outfit'] font-semibold">{pkg.price}EUR</span>
                    )}
                  </div>
                  <span className="text-sm text-[#A1A1AA]">{display.period}</span>
                </div>

                <ul className="space-y-4 mb-8 flex-grow">
                  {localizedFeatures(id, pkg.features).map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                      <span className="text-[#A1A1AA]">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handlePurchase(id)}
                  disabled={loading}
                  className={`w-full py-6 rounded-full font-medium ${
                    display.popular
                      ? 'bg-white text-black hover:bg-white/90'
                      : 'bg-[#0055FF] hover:bg-[#3377FF] text-white'
                  }`}
                  data-testid={`pricing-cta-${id}`}
                >
                  {loading ? t('pricing.loading') : display.cta}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Subscription Add-ons */}
        {(subBasic || subPro) && (
          <div className="mt-16 p-8 rounded-3xl bg-[#111111] border border-white/10">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Repeat className="w-6 h-6 text-[#0055FF]" />
              <h3 className="font-['Outfit'] text-2xl font-medium">{t('pricing.addon.title')}</h3>
            </div>
            <p className="text-center text-[#A1A1AA] mb-8">{t('pricing.addon.desc')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {subBasic && (
                <div className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-[#0055FF]/30 transition-colors">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-['Outfit'] font-medium">{subBasic.name}</span>
                    <span className="text-[#0055FF] font-semibold">{subBasic.price}{t('pricing.perMonth')}</span>
                  </div>
                  <ul className="space-y-2 text-sm text-[#A1A1AA] mb-6">
                    {localizedFeatures('subscription_basic', subBasic.features).map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                  <Button
                    onClick={() => handleSubscription('subscription_basic')}
                    disabled={loading}
                    className="w-full bg-transparent border border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white rounded-full"
                    data-testid="subscribe-basic-btn"
                  >
                    {t('pricing.addon.subscribe')}
                  </Button>
                </div>
              )}
              {subPro && (
                <div className="p-6 rounded-2xl bg-gradient-to-b from-[#0055FF]/10 to-transparent border border-[#0055FF]/30">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-['Outfit'] font-medium">{subPro.name}</span>
                    <span className="text-[#0055FF] font-semibold">{subPro.price}{t('pricing.perMonth')}</span>
                  </div>
                  <ul className="space-y-2 text-sm text-[#A1A1AA] mb-6">
                    {localizedFeatures('subscription_pro', subPro.features).map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                  <Button
                    onClick={() => handleSubscription('subscription_pro')}
                    disabled={loading}
                    className="w-full bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full"
                    data-testid="subscribe-pro-btn"
                  >
                    {t('pricing.addon.subscribe')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* B2B White-Label - Sezione separata */}
        {b2b && (
          <div className="mt-16 p-8 rounded-3xl bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-[#F59E0B]/20 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#F59E0B]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-8 h-8 text-[#F59E0B]" />
                  <span className="text-xs uppercase tracking-[0.15em] text-[#F59E0B] font-semibold">{t('pricing.enterprise.eyebrow')}</span>
                </div>
                <h3 className="font-['Outfit'] text-3xl font-medium mb-3">{b2b.name}</h3>
                <p className="text-[#A1A1AA] mb-6">{t('pricing.enterprise.desc')}</p>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-3xl font-['Outfit'] font-bold text-[#F59E0B]">{t('pricing.enterprise.from')} {b2b.price}EUR</span>
                  <span className="text-sm text-[#A1A1AA]">{t('pricing.enterprise.setupNote')}</span>
                </div>
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-medium rounded-full px-8 py-6"
                  data-testid="pricing-cta-b2b_custom"
                >
                  {t('pricing.enterprise.cta')}
                </Button>
              </div>
              <div>
                <ul className="space-y-3">
                  {localizedFeatures('b2b_custom', b2b.features).map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                      <span className="text-[#A1A1AA]">{feature}</span>
                    </li>
                  ))}
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                    <span className="text-[#A1A1AA]">{t('pricing.enterprise.sla')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                    <span className="text-[#A1A1AA]">{t('pricing.enterprise.custom')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PricingSection;
